import { api } from './apiClient';
import type { ChatSource } from './modelsService';
import type { Artifact } from '../types';

export interface Conversation {
  id: string;
  agentId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messageCount?: number;
  costUsd?: number;
  lastMessage?: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  costUsd?: number;
  sources?: ChatSource[];
  artifacts?: Artifact[];
  createdAt: string;
}

export interface SendResult {
  message: ChatMessage;
  cost?: { costUsd: number; monthlySpentUsd: number; monthlyCapUsd: number; overPerRun: boolean };
}

export interface ChatAttachment {
  id: string;
  conversationId: string;
  name: string;
  type: string;
  sizeBytes: number;
  indexStatus: 'pending' | 'indexing' | 'ready' | 'error';
  chunkCount?: number;
  error?: string;
  createdAt: string;
}

export const chatService = {
  async listConversations(agentId: string): Promise<Conversation[]> {
    const res = await api.get<{ conversations: Conversation[] }>(`/chat/conversations?agentId=${encodeURIComponent(agentId)}`);
    return res.conversations;
  },
  async create(agentId: string): Promise<Conversation> {
    const res = await api.post<{ conversation: Conversation }>('/chat/conversations', { agentId });
    return res.conversation;
  },
  async get(id: string): Promise<{ conversation: Conversation; messages: ChatMessage[] }> {
    return api.get<{ conversation: Conversation; messages: ChatMessage[] }>(`/chat/conversations/${encodeURIComponent(id)}`);
  },
  async rename(id: string, title: string): Promise<void> {
    await api.patch(`/chat/conversations/${encodeURIComponent(id)}`, { title });
  },
  async remove(id: string): Promise<void> {
    await api.del(`/chat/conversations/${encodeURIComponent(id)}`);
  },
  async send(id: string, content: string): Promise<SendResult> {
    return api.post<SendResult>(`/chat/conversations/${encodeURIComponent(id)}/messages`, { content });
  },

  // --- Anexos efêmeros (RAG só nesta conversa) ---
  async listAttachments(id: string): Promise<ChatAttachment[]> {
    const res = await api.get<{ attachments: ChatAttachment[] }>(`/chat/conversations/${encodeURIComponent(id)}/attachments`);
    return res.attachments;
  },
  async uploadAttachments(id: string, files: File[]): Promise<ChatAttachment[]> {
    const form = new FormData();
    files.forEach((f) => form.append('files', f));
    const res = await api.upload<{ attachments: ChatAttachment[] }>(`/chat/conversations/${encodeURIComponent(id)}/attachments`, form);
    return res.attachments;
  },
  async removeAttachment(id: string, attId: string): Promise<void> {
    await api.del(`/chat/conversations/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attId)}`);
  },
};
